import { TollAreaModel, TollAreaModelFactory } from "@/core/models/toll-area/toll-area.model";
import { AddressRepo } from "@/core/repos/address.repo";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import container from "@/lib/di/container";
import { useState, useEffect } from "react";
import { FilterBar, FilterInputTemplate, FilterInputType, handleAddressChange } from "../../components/filter/FilterBar";
import { FilterSearchPayload, DataTable } from "../../components/table/DataTable";
import { DetailModal } from "./modals/DetailModal";
import { Button, Dropdown, message, Tooltip } from "antd";
import { InfoCircleOutlined, MoreOutlined, TeamOutlined } from "@ant-design/icons";
import { useTitleDataStore } from "@/core/stores/title-data.store";
import { DeleteButton } from "../../components/table/DeleteButton";
import { useNavigate } from "react-router";
import { UserDetail } from "@/core/models/auth/auth.model";
import { UserRepo } from "@/core/repos/user.repo";

const tollAreaRepo = container.get(TollAreaRepo);
const addressRepo = container.get(AddressRepo);
const userRepo = container.get(UserRepo);

export default function TollAreaPage() {
    const navigate = useNavigate();
    const titleData = useTitleDataStore();
    const [modalData, setModalData] = useState<TollAreaModel>(TollAreaModelFactory.getEmpty())
    const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(new Map())
    const [selectedRows, setSelectedRows] = useState<TollAreaModel[]>([]);
    const [isDetailModal, setDetailModal] = useState(false);
    const [users, setUsers] = useState<UserDetail[]>([]);
    const [deleting, setDeleting] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [filterTemplates, setFilterTemplates] = useState<FilterInputTemplate[]>([
        {
            dataIndex: "search",
            label: "Tìm kiếm",
            placeholder: "Nhập nội dung tìm kiếm",
            type: FilterInputType.text,
        },
        {
            dataIndex: "provinceId",
            label: "Tỉnh/ TP",
            placeholder: "Chọn Tỉnh/ TP",
            type: FilterInputType.select,
            options: []
        },
        {
            dataIndex: "districtId",
            label: "Quận/ Huyện",
            placeholder: "Chọn Quận/ Huyện",
            type: FilterInputType.select,
            options: []
        },
        {
            dataIndex: "wardId",
            label: "Xã/ Phường",
            placeholder: "Chọn Xã/ Phường",
            type: FilterInputType.select,
            options: []
        }
    ])

    //componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);

    const populateAddressSelection = async () => {
        const provinces = (await addressRepo.getAllProvinces()).map(data => ({name: data[1].name, value: data[0]}));
        const templates: FilterInputTemplate[] = [...filterTemplates];
        const provinceTemplate = templates.find(template => template.dataIndex === "provinceId");
        if (provinceTemplate) {
            provinceTemplate.options = provinces;
        }
        setFilterTemplates(templates);
    }

    const populateFactoryUsers = async () => {
        const _users = (await userRepo.getFromFactory());
        setUsers(_users);
    }

    const init = async () => {
        titleData.setTitleActions({
            text: "Thêm Khu vực",
            onPress: () => {
                showDetail(TollAreaModelFactory.getEmpty())
            }
        })
        await populateAddressSelection();
        await populateFactoryUsers();
    }

    async function onFilterChange(map: Map<string, FilterSearchPayload>, changes: string[]) {
        await handleAddressChange(map, changes, filterTemplates, setFilterTemplates);
        setFilterMap(new Map(map));
    }

    function getUserName(userId: string | undefined): string {
        if (!userId) return "";
        const user = users.find(u => u.id === userId);
        return user ? `${user.userName.toUpperCase()} - ${user.fullName}` : "";
    }

    function onRowSelect(rows: TollAreaModel[]) {
        setSelectedRows([...rows]);
    }

    function showContracts(record: TollAreaModel) {
        navigate(encodeURI(`/customer/contracts?tollAreaId=${record.id}`));
    }

    const showDetail = (record: TollAreaModel) => {
        setModalData(record);
        setDetailModal(true);
    };

    const deleteSelected =  async () => {
        setDeleting(true);
        const result = await tollAreaRepo.deleteMany(selectedRows);
        messageApi.success({
            content: `Bạn đã xoá thành công ${result.success}/${result.total} bản ghi`
        })
        setDeleting(false);
    }

    return (
    <div className="px-4 py-6">
        {contextHolder}
        <FilterBar setMap={setFilterMap} onChange={onFilterChange} templates={filterTemplates} map={filterMap} actions={[
            <div key={1}>
                <DeleteButton loading={deleting} length={selectedRows.length} onConfirm={deleteSelected} noGap/>
            </div>
        ]}/>
        <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
            <DataTable<TollAreaModel>
                columns={[
                    { dataIndex: 'code', name: 'Mã' },
                    { dataIndex: 'name', name: 'Tên khu vực' },
                    { dataIndex: 'provinceName', name: 'Tỉnh/ TP' },
                    { dataIndex: 'wardName', name: 'Phường/ Xã' },
                    { dataIndex: 'readerId', name: 'Người đọc', pipe: (value) => getUserName(value) },
                    { dataIndex: 'exportDate', name: 'Ngày chốt số hàng tháng' },
                    {
                        name: 'Thao tác',
                        render: (_, record) => (
                                <div className="flex flex-row px-1 items-center justify-center">
                                    <Tooltip title="Sửa khu vực"><Button type="text" icon={<InfoCircleOutlined />} onClick={() => showDetail(record)}/></Tooltip>
                                    <Dropdown menu={{items: [
                                        {
                                            key: '1',
                                            label: <p>Danh sách hộ dùng nước</p>,
                                            icon: <TeamOutlined />,
                                            onClick: () => showContracts(record)
                                        },
                                    ]}} trigger={["hover"]}>
                                        <Button type="text" icon={<MoreOutlined />}/>
                                    </Dropdown>
                                    
                                </div>
                            ),
                        fixed: "right"
                    },
                ]}
                repository={tollAreaRepo}
                filterMap={filterMap}
                onSelect={onRowSelect}
            ></DataTable>
        </div>
        <DetailModal isModalOpen={isDetailModal} setIsModalOpen={setDetailModal} data={modalData}></DetailModal>
    </div>)
}